import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CalendarBlank,
  Plus,
  Trash,
  CheckCircle,
  XCircle,
  Clock,
  CircleNotch,
  Lightning,
  Robot,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SchedulePage = () => {
  const [schedules, setSchedules] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [taskName, setTaskName] = useState("");
  const [templateType, setTemplateType] = useState("cashinout");
  const [loading, setLoading] = useState(false);

  // Auto-schedule state
  const [autoConfig, setAutoConfig] = useState({
    enabled: false,
    per_hour: 3,
    profile_ids: [],
    hours_from: 8,
    hours_to: 23,
  });
  const [autoProfileIds, setAutoProfileIds] = useState([]);
  const [showAutoSetup, setShowAutoSetup] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/schedules`);
      setSchedules(res.data);
    } catch { /* */ }
  };

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/profiles`);
      setProfiles(res.data);
    } catch { /* */ }
  };

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API}/folders`);
      setFolders(res.data);
    } catch { /* */ }
  };

  const fetchAutoConfig = async () => {
    try {
      const res = await axios.get(`${API}/auto-schedule`);
      setAutoConfig(res.data);
      setAutoProfileIds(res.data.profile_ids || []);
    } catch { /* */ }
  };

  useEffect(() => {
    fetchSchedules();
    fetchProfiles();
    fetchFolders();
    fetchAutoConfig();
    const interval = setInterval(fetchSchedules, 15000);
    return () => clearInterval(interval);
  }, []);

  // Quick toggle auto-schedule ON/OFF
  const toggleAuto = async (enabled) => {
    try {
      const payload = { ...autoConfig, enabled };
      await axios.post(`${API}/auto-schedule`, payload);
      setAutoConfig(prev => ({ ...prev, enabled }));
      toast.success(enabled ? "Авто-расписание включено" : "Авто-расписание выключено");
    } catch {
      toast.error("Ошибка сохранения");
    }
  };

  const saveAutoConfig = async () => {
    if (autoProfileIds.length === 0) {
      toast.error("Выберите профили для авто-расписания");
      return;
    }
    setSavingAuto(true);
    try {
      const payload = {
        ...autoConfig,
        profile_ids: autoProfileIds,
        enabled: true,
      };
      await axios.post(`${API}/auto-schedule`, payload);
      setAutoConfig(payload);
      toast.success("Авто-расписание сохранено и включено");
      setShowAutoSetup(false);
    } catch {
      toast.error("Ошибка сохранения");
    } finally {
      setSavingAuto(false);
    }
  };

  const templateLabels = { cashinout: "Cashinout", betboom: "BetBoom", novapay: "NovaPay", "1win": "1Win" };

  const handleCreate = async () => {
    if (!selectedIds.length) { toast.error("Выберите профили"); return; }
    if (!scheduledDate || !scheduledTime) { toast.error("Укажите дату и время"); return; }
    const dt = new Date(`${scheduledDate}T${scheduledTime}:00`);
    setLoading(true);
    try {
      const tplLabel = templateLabels[templateType] || templateType;
      await axios.post(`${API}/schedules`, {
        profile_ids: selectedIds,
        scheduled_at: dt.toISOString(),
        name: taskName || `${tplLabel} × ${selectedIds.length}`,
        template_type: templateType,
      });
      toast.success("Задание создано");
      setShowCreate(false);
      setSelectedIds([]);
      setScheduledDate("");
      setScheduledTime("");
      setTaskName("");
      setTemplateType("cashinout");
      fetchSchedules();
    } catch {
      toast.error("Ошибка создания");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/schedules/${id}`);
      toast.success("Удалено");
      fetchSchedules();
    } catch { toast.error("Ошибка"); }
  };

  const clearDone = async () => {
    const done = schedules.filter(s => s.status === "done" || s.status === "error");
    for (const s of done) {
      try { await axios.delete(`${API}/schedules/${s.id}`); } catch {}
    }
    toast.success(`Удалено ${done.length} заданий`);
    fetchSchedules();
  };

  const toggleProfile = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleAutoProfile = (id) =>
    setAutoProfileIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const selectAll = () =>
    setSelectedIds(selectedIds.length === profiles.length ? [] : profiles.map(p => p.id));

  const selectAllAuto = () =>
    setAutoProfileIds(autoProfileIds.length === profiles.length ? [] : profiles.map(p => p.id));

  const getProfileName = (pid) => {
    const p = profiles.find(x => x.id === pid);
    return p ? p.name : pid.slice(0, 8);
  };

  const getFolderName = (fid) => {
    const f = folders.find(x => x.id === fid);
    return f ? f.name : "Без папки";
  };

  // Group profiles by folder — only show "Ban" folder
  const groupedProfiles = folders
    .filter(f => f.name === "Ban")
    .map(f => ({
      folder: f,
      profiles: profiles.filter(p => p.folder_id === f.id),
    })).filter(g => g.profiles.length > 0);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const statusIcon = (status) => {
    if (status === "done") return <CheckCircle size={18} weight="fill" className="text-[#4CAF50]" />;
    if (status === "error") return <XCircle size={18} weight="fill" className="text-[#F44336]" />;
    if (status === "running") return <CircleNotch size={18} className="text-[#2196F3] animate-spin" />;
    return <Clock size={18} className="text-[#FF9800]" />;
  };

  const statusText = (status) => {
    if (status === "done") return "OK";
    if (status === "error") return "Ошибка";
    if (status === "running") return "...";
    return "Ожидание";
  };

  const [parsingPins, setParsingPins] = useState(false);
  const [pinProgress, setPinProgress] = useState(null);

  const handleParsePins = async () => {
    setParsingPins(true);
    setPinProgress(null);
    try {
      // Get bot username from settings
      let botUser = "ozon_48230_block_bot";
      try {
        const botRes = await axios.get(`${API}/settings/bot-username`);
        botUser = botRes.data.username || botUser;
      } catch { /* use default */ }
      await axios.post(`${API}/telegram/parse-pins`, {
        bot_username: botUser,
      });
      // Poll progress
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/telegram/parse-pins/status`);
          setPinProgress(res.data);
          if (res.data.status === "done" || res.data.status === "error") {
            clearInterval(poll);
            setParsingPins(false);
            fetchProfiles();
            if (res.data.status === "done") {
              toast.success(`PIN: ${res.data.updated} обновлено, ${res.data.errors} ошибок`);
            } else {
              toast.error(res.data.current || "Ошибка парсинга");
            }
          }
        } catch { clearInterval(poll); setParsingPins(false); }
      }, 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ошибка запуска");
      setParsingPins(false);
    }
  };

  // Block check state
  const [checkingBlocks, setCheckingBlocks] = useState(false);
  const [blockProgress, setBlockProgress] = useState(null);
  const [blockFolder, setBlockFolder] = useState("Ban");

  const handleCheckBlocks = async (folder = blockFolder) => {
    setCheckingBlocks(true);
    setBlockProgress(null);
    try {
      await axios.post(`${API}/block-check/start`, { folder });
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/block-check/status`);
          setBlockProgress(res.data);
          if (res.data.status === "done" || res.data.status === "error") {
            clearInterval(poll);
            setCheckingBlocks(false);
            fetchProfiles();
            if (res.data.status === "done") {
              toast.success(`Блокировки: ${res.data.blocked} блок, ${res.data.unblocked} актив, ${res.data.arrested || 0} арест, ${res.data.close_account || 0} закр, ${res.data.errors} ошиб`);
            } else {
              toast.error(res.data.current || "Ошибка проверки");
            }
          }
        } catch { clearInterval(poll); setCheckingBlocks(false); }
      }, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ошибка запуска");
      setCheckingBlocks(false);
    }
  };

  const getTemplateBadge = (tpl) => {
    const colors = {
      cashinout: "bg-[#00c853]/20 text-[#00c853]",
      betboom: "bg-[#f9d547]/20 text-[#f9d547]",
      novapay: "bg-[#7c3aed]/20 text-[#a78bfa]",
      "1win": "bg-[#22c55e]/20 text-[#22c55e]",
    };
    const labels = { cashinout: "CIO", betboom: "BB", novapay: "NP", "1win": "1W" };
    return (
      <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${colors[tpl] || "bg-gray-500/20 text-gray-400"}`}>
        {labels[tpl] || tpl}
      </span>
    );
  };

  const pendingSchedules = schedules.filter(s => s.status === "pending");
  const activeSchedules = schedules.filter(s => s.status === "running");
  const historySchedules = schedules.filter(s => s.status === "done" || s.status === "error");

  return (
    <div className="p-6 max-w-[1200px]" data-testid="schedule-page">
      {/* Auto-schedule panel */}
      <div className="bg-[#1A1D24] border border-[#252A33] rounded-lg p-4 mb-6" data-testid="auto-schedule-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Robot size={22} weight="duotone" className={autoConfig.enabled ? "text-[#4CAF50]" : "text-[#5C6370]"} />
            <div>
              <div className="font-medium text-sm flex items-center gap-2">
                Авто-расписание
                {autoConfig.enabled && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#4CAF50]/20 text-[#4CAF50]">ON</span>
                )}
              </div>
              <div className="text-xs text-[#5C6370]">
                {autoConfig.enabled
                  ? `${autoConfig.per_hour} заявок/час, ${autoConfig.hours_from}:00–${autoConfig.hours_to}:00 MSK, ${autoConfig.profile_ids.length} профилей`
                  : "Выключено"
                }
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAutoSetup(true)}
              className="text-xs border-[#252A33] hover:bg-[#252A33]"
              data-testid="auto-setup-btn"
            >
              <Lightning size={14} className="mr-1" />
              Настроить
            </Button>
            <Switch
              checked={autoConfig.enabled}
              onCheckedChange={toggleAuto}
              disabled={!autoConfig.profile_ids?.length}
              data-testid="auto-toggle"
            />
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <CalendarBlank size={22} weight="duotone" className="text-[#FF9800]" />
          Расписание
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleParsePins}
            disabled={parsingPins}
            className="text-xs border-[#252A33] hover:bg-[#252A33]"
            data-testid="parse-pins-btn"
          >
            {parsingPins ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <Lightning size={14} className="mr-1" />}
            {parsingPins && pinProgress
              ? `PIN ${pinProgress.progress}/${pinProgress.total} (${pinProgress.updated} ok)`
              : parsingPins ? "Запуск..." : "Спарсить PIN"
            }
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCheckBlocks("Ban")}
            disabled={checkingBlocks}
            className="text-xs border-[#252A33] hover:bg-[#252A33]"
            data-testid="check-blocks-ban-btn"
          >
            {checkingBlocks && blockProgress?.folder === "Ban" ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <Lightning size={14} className="mr-1" />}
            {checkingBlocks && blockProgress
              ? `Блок ${blockProgress.progress}/${blockProgress.total}`
              : "Блокировки Ban"
            }
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCheckBlocks("Активный")}
            disabled={checkingBlocks}
            className="text-xs border-[#252A33] hover:bg-[#252A33]"
            data-testid="check-blocks-active-btn"
          >
            {checkingBlocks && blockProgress?.folder === "Активный" ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <Lightning size={14} className="mr-1" />}
            {checkingBlocks && blockProgress
              ? `Блок ${blockProgress.progress}/${blockProgress.total}`
              : "Блокировки Активный"
            }
          </Button>
          {historySchedules.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearDone}
              className="text-xs border-[#252A33] hover:bg-[#252A33] text-[#8A9099]"
              data-testid="clear-history-btn"
            >
              <Trash size={14} className="mr-1" />
              Очистить историю
            </Button>
          )}
          <Button
            onClick={() => setShowCreate(true)}
            size="sm"
            className="bg-[#1A5CFF] hover:bg-[#1A5CFF]/80 text-xs"
            data-testid="create-schedule-btn"
          >
            <Plus size={14} className="mr-1" />
            Ручное задание
          </Button>
        </div>
      </div>

      {/* Pending + Running */}
      {(pendingSchedules.length > 0 || activeSchedules.length > 0) && (
        <div className="space-y-2 mb-4">
          {[...activeSchedules, ...pendingSchedules].map((s) => (
            <div
              key={s.id}
              className="bg-[#1A1D24] border border-[#252A33] rounded-lg px-4 py-3 flex items-center justify-between"
              data-testid={`schedule-item-${s.id}`}
            >
              <div className="flex items-center gap-3">
                {statusIcon(s.status)}
                <div>
                  <span className="text-sm font-medium">
                    {s.name}
                    {s.auto_generated && (
                      <Robot size={12} className="inline ml-1 text-[#5C6370]" />
                    )}
                  </span>
                  <div className="text-[10px] text-[#5C6370] flex items-center gap-1">
                    {getTemplateBadge(s.template_type || "cashinout")}
                    {formatDate(s.scheduled_at)} | {(s.profile_ids || []).map(pid => getProfileName(pid)).join(", ")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  s.status === "running" ? "bg-[#2196F3]/20 text-[#2196F3]" : "bg-[#FF9800]/20 text-[#FF9800]"
                }`}>
                  {statusText(s.status)}
                </span>
                <button
                  onClick={() => handleDelete(s.id)}
                  className="icon-btn w-6 h-6 hover:text-[#F44336]"
                  data-testid={`delete-schedule-${s.id}`}
                >
                  <Trash size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      {historySchedules.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-[#5C6370] mb-1">История</div>
          {historySchedules.slice(0, 50).map((s) => (
            <div
              key={s.id}
              className="bg-[#1A1D24]/60 border border-[#252A33]/50 rounded-lg px-4 py-2"
              data-testid={`schedule-item-${s.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {statusIcon(s.status)}
                  <span className="text-xs">
                    {s.name}
                    {s.auto_generated && <Robot size={11} className="inline ml-1 text-[#5C6370]" />}
                  </span>
                  <span className="text-[10px] text-[#5C6370]">{formatDate(s.scheduled_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {s.results?.map((r, i) => (
                    <span key={i} className="text-[10px] text-[#8A9099]">
                      {r.profile_name}: {r.success
                        ? (r.files_count ? `${r.files_count} скринов` : "OK")
                        : <span className="text-[#F44336]">{(r.error || "err").slice(0, 30)}</span>
                      }
                    </span>
                  ))}
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="icon-btn w-5 h-5 hover:text-[#F44336] opacity-50 hover:opacity-100"
                  >
                    <Trash size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {schedules.length === 0 && (
        <div className="text-center py-16 text-[#5C6370]">
          <CalendarBlank size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Нет заданий</p>
          <p className="text-xs mt-1">Включите авто-расписание или создайте ручное задание</p>
        </div>
      )}

      {/* Manual create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#1A1D24] border-[#252A33] max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Plus size={18} className="text-[#1A5CFF]" />
              Ручное задание
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <Label className="text-xs text-[#8A9099]">Название</Label>
              <Input
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                placeholder="Задание"
                className="mt-1 bg-[#0D0F12] border-[#252A33]"
                data-testid="schedule-name-input"
              />
            </div>
            <div>
              <Label className="text-xs text-[#8A9099]">Шаблон скриншотов</Label>
              <select
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value)}
                className="mt-1 w-full h-9 px-3 rounded-md bg-[#0D0F12] border border-[#252A33] text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#1A5CFF]"
                data-testid="template-type-select"
              >
                <option value="cashinout">Cashinout (крипто-кошелёк)</option>
                <option value="betboom">BetBoom (букмекер)</option>
                <option value="novapay">NovaPay / ONESIX (крипто)</option>
                <option value="1win">1Win (казино/ставки)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#8A9099]">Дата</Label>
                <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
                  className="mt-1 bg-[#0D0F12] border-[#252A33]" data-testid="schedule-date-input" />
              </div>
              <div>
                <Label className="text-xs text-[#8A9099]">Время</Label>
                <Input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)}
                  className="mt-1 bg-[#0D0F12] border-[#252A33]" data-testid="schedule-time-input" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-[#8A9099]">Профили</Label>
                <button onClick={selectAll} className="text-[10px] text-[#1A5CFF] hover:underline">
                  {selectedIds.length === profiles.length ? "Снять" : "Все"}
                </button>
              </div>
              <div className="max-h-[250px] overflow-y-auto border border-[#252A33] rounded-lg">
                {groupedProfiles.map(g => (
                  <div key={g.folder.id}>
                    <div className="px-3 py-1 bg-[#252A33]/50 text-[10px] text-[#8A9099] font-medium sticky top-0 flex items-center justify-between">
                      <span>{g.folder.name} ({g.profiles.length})</span>
                      <button
                        className="text-[#1A5CFF] hover:underline"
                        onClick={() => {
                          const ids = g.profiles.map(p => p.id);
                          const allSelected = ids.every(id => selectedIds.includes(id));
                          setSelectedIds(prev => allSelected
                            ? prev.filter(x => !ids.includes(x))
                            : [...new Set([...prev, ...ids])]
                          );
                        }}
                      >
                        {g.profiles.every(p => selectedIds.includes(p.id)) ? "Снять" : "Все"}
                      </button>
                    </div>
                    {g.profiles.map(p => (
                      <label key={p.id} className="flex items-center gap-2 px-3 py-1 hover:bg-[#252A33]/50 cursor-pointer text-xs">
                        <Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={() => toggleProfile(p.id)} />
                        <span className="font-mono">{p.name}</span>
                        {p.ozon?.fullname && <span className="text-[10px] text-[#5C6370]">({p.ozon.fullname})</span>}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-[#5C6370] mt-1">Выбрано: {selectedIds.length}</div>
            </div>
            <Button onClick={handleCreate} disabled={loading || !selectedIds.length || !scheduledDate || !scheduledTime}
              className="w-full bg-[#FF9800] hover:bg-[#FF9800]/80 text-black font-medium text-sm" data-testid="submit-schedule-btn">
              {loading ? "Создание..." : "Создать"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-schedule setup dialog */}
      <Dialog open={showAutoSetup} onOpenChange={setShowAutoSetup}>
        <DialogContent className="bg-[#1A1D24] border-[#252A33] max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Robot size={18} className="text-[#4CAF50]" />
              Настройка авто-расписания
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <Label className="text-xs text-[#8A9099]">Заявок в час</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={autoConfig.per_hour}
                onChange={(e) => setAutoConfig(prev => ({ ...prev, per_hour: parseInt(e.target.value) || 3 }))}
                className="mt-1 bg-[#0D0F12] border-[#252A33] w-24"
                data-testid="auto-per-hour"
              />
            </div>
            <div>
              <Label className="text-xs text-[#8A9099]">Шаблон скриншотов</Label>
              <select
                value={autoConfig.template_type || "cashinout"}
                onChange={(e) => setAutoConfig(prev => ({ ...prev, template_type: e.target.value }))}
                className="mt-1 w-full h-9 px-3 rounded-md bg-[#0D0F12] border border-[#252A33] text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#1A5CFF]"
                data-testid="auto-template-type"
              >
                <option value="cashinout">Cashinout (крипто-кошелёк)</option>
                <option value="betboom">BetBoom (букмекер)</option>
                <option value="novapay">NovaPay / ONESIX (крипто)</option>
                <option value="1win">1Win (казино/ставки)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#8A9099]">С (час MSK)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={autoConfig.hours_from}
                  onChange={(e) => setAutoConfig(prev => ({ ...prev, hours_from: parseInt(e.target.value) || 0 }))}
                  className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  data-testid="auto-hours-from"
                />
              </div>
              <div>
                <Label className="text-xs text-[#8A9099]">До (час MSK)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={autoConfig.hours_to}
                  onChange={(e) => setAutoConfig(prev => ({ ...prev, hours_to: parseInt(e.target.value) || 23 }))}
                  className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  data-testid="auto-hours-to"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-[#8A9099]">Профили (рандомно из выбранных)</Label>
                <button onClick={selectAllAuto} className="text-[10px] text-[#4CAF50] hover:underline" data-testid="auto-select-all">
                  {autoProfileIds.length === profiles.length ? "Снять" : "Все"}
                </button>
              </div>
              <div className="max-h-[250px] overflow-y-auto border border-[#252A33] rounded-lg">
                {groupedProfiles.map(g => (
                  <div key={g.folder.id}>
                    <div className="px-3 py-1 bg-[#252A33]/50 text-[10px] text-[#8A9099] font-medium sticky top-0 flex items-center justify-between">
                      <span>{g.folder.name} ({g.profiles.length})</span>
                      <button
                        className="text-[#4CAF50] hover:underline"
                        onClick={() => {
                          const ids = g.profiles.map(p => p.id);
                          const allSelected = ids.every(id => autoProfileIds.includes(id));
                          setAutoProfileIds(prev => allSelected
                            ? prev.filter(x => !ids.includes(x))
                            : [...new Set([...prev, ...ids])]
                          );
                        }}
                      >
                        {g.profiles.every(p => autoProfileIds.includes(p.id)) ? "Снять" : "Все"}
                      </button>
                    </div>
                    {g.profiles.map(p => (
                      <label key={p.id} className="flex items-center gap-2 px-3 py-1 hover:bg-[#252A33]/50 cursor-pointer text-xs">
                        <Checkbox checked={autoProfileIds.includes(p.id)} onCheckedChange={() => toggleAutoProfile(p.id)} />
                        <span className="font-mono">{p.name}</span>
                        {p.ozon?.fullname && <span className="text-[10px] text-[#5C6370]">({p.ozon.fullname})</span>}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-[#5C6370] mt-1">Выбрано: {autoProfileIds.length}</div>
            </div>
            <div className="bg-[#0D0F12] border border-[#252A33] rounded-lg p-3 text-xs text-[#8A9099]">
              Система будет автоматически создавать <b className="text-[#ccc]">{autoConfig.per_hour}</b> заявок в час
              с <b className="text-[#ccc]">{autoConfig.hours_from}:00</b> до <b className="text-[#ccc]">{autoConfig.hours_to}:00</b> MSK.
              Шаблон: <b className="text-[#ccc]">{templateLabels[autoConfig.template_type] || "Cashinout"}</b>.
              Время каждой заявки — случайное. Профиль выбирается рандомно из списка.
            </div>
            <Button
              onClick={saveAutoConfig}
              disabled={savingAuto || autoProfileIds.length === 0}
              className="w-full bg-[#4CAF50] hover:bg-[#4CAF50]/80 text-white font-medium text-sm"
              data-testid="save-auto-btn"
            >
              {savingAuto ? "Сохранение..." : "Сохранить и включить"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulePage;
